const NavigationBar = function ({ cobrand = "", appearance = "opaque", sticky = true, stickyLimit = 0 }) {
    const LogoConfig = Object.freeze({
        "direct-color": { logo: "direct", arrow: "orange" },
        "livingston-white": { logo: "livingston", arrow: "white" },
        "livingston-color": { logo: "livingston", arrow: "blue" },
    });

    let settings = {
        // function arguments are not used directly
        // Instead they are used to set the following settings
        // depending on environment
        cobrand: cobrand,
        transparent: appearance === "transparent",
        sticky: sticky === "true" || sticky === true,
        stickyLimit: stickyLimit,
        primaryLogo: "livingston-white",
        secondaryLogo: "livingston-color",
        // the amount of pixels the popover is allowed
        // to extend beyound main menu row
        menuRelativeMargin: -100,
    };

    const TimingFunctions = Object.freeze({
        linear: (t) => t,
        easeIn: (t) => t * t,
        easeOut: (t) => t * (2 - t),
        easeInOut: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
        easeInOutQuint: (t) => ((t *= 2) < 1 ? 0.5 * t * t * t * t * t : 0.5 * ((t -= 2) * t * t * t * t + 2)),
    });

    const state = {
        selected: null,
        currentLogo: "livingston-white",
        returnScrollPos: 0,
        scrollY: 0,
    };

    const desktop = {
        popoverVisible: false,
    };

    const mobile = {
        enabled: false,
        subMenuVisible: false,
        popoverVisible: false,
    };

    const navBar = {
        y: 0,
        scale: 0,
        height: 0,
        scrollTrigger: 0,
        locked: false,
    };

    const view = {
        min: 900,
        max: 1440, // 1280,
    };

    const fade = {
        min: 20,
        max: 250,
    };

    let timeout;
    let clicked = false;

    let root;
    let logo;
    let logoBox;
    let nav;
    let navBg;
    let arrow;
    let popover;
    let popoverBg;
    let popoverContent;
    let mobilePopover;
    let backBtn;
    let hamburgerBtn;

    init();

    function init() {
        root = document.querySelector(".nav");
        logo = root.querySelector(".logo");
        logoBox = root.querySelector(".logo-box");
        nav = root.querySelector(".main-menu");
        navBg = root.querySelector(".nav-bar-bg");
        arrow = root.querySelector(".popover-arrow");
        popover = root.querySelector(".popover");
        popoverBg = root.querySelector(".popover-bg");
        popoverContent = root.querySelector(".popover-content");
        mobilePopover = root.querySelector(".mobile-popover");
        backBtn = root.querySelector(".back-button");
        hamburgerBtn = root.querySelector(".hamburger-button");
        callBtn = root.querySelector(".call-button");

        // shared listeners
        popoverBg.addEventListener("touchstart", closePopover);
        popoverBg.addEventListener("mousedown", closePopover);
        window.addEventListener("scroll", scroll);
        window.addEventListener("resize", resize);
        window.addEventListener("keydown", keyDown);

        // reset root style to remove all element styles
        root.style.display = "initial";

        // defines whether to show minimal hamburger menu
        mobile.enabled = window.innerWidth <= 900;

        // mobile version
        if (mobile.enabled) {
            // override settings for mobile
            settings.cobrand = "";
            settings.transparent = false;
            settings.sticky = false;
            settings.stickyLimit = window.innerHeight;

            // setup mobile elements
            mobilePopover.appendChild(nav);
            mobilePopover.appendChild(popover);
            nav.appendChild(callBtn);
            popover.style.left = `100%`;

            // setup mobile event listeners
            for (const item of nav.children) {
                item.addEventListener("click", () => {
                    showMobileSubMenu(item);
                });
            }
            backBtn.addEventListener("click", closeMobileSubMenu);
            hamburgerBtn.addEventListener("click", toggleMobilePopover);
        }
        // desktop version
        else {
            // desktop specific settings base on function args
            settings.cobrand = cobrand;
            settings.transparent = appearance === "transparent";
            settings.sticky = sticky;
            settings.stickyLimit = stickyLimit;

            // setup desktop elements
            logoBox.insertAdjacentElement("afterend", nav);
            root.appendChild(popover);

            // setup desktop event listeners
            for (const item of nav.children) {
                // mouse only
                item.addEventListener("touchend", () => {
                    togglePopover(item);
                    clicked = true;
                });
                item.addEventListener("mouseenter", () => {
                    if (!clicked) openPopover(item);
                });
                // mouse only
                item.addEventListener("mouseleave", () => {
                    if (!clicked) delayedClose();
                });
                // touch + mouse
                item.addEventListener("click", () => {
                    if (!clicked) togglePopover(item);
                    clicked = false;
                });
            }
            popover.addEventListener("mouseenter", cancelClose);
            popover.addEventListener("mouseleave", delayedClose);
            popover.addEventListener("click", closePopover);
        }

        // configures sticky behaviour
        setSticky(settings.sticky, settings.stickyLimit);

        // initial nav bar scale
        root.style.setProperty("--navBarScale", settings.transparent ? 1 : 0);

        // setup logos based on settings
        setupLogos(cobrand);

        // init by draw + scroll
        resize();
        scroll();
    }

    function dispose() {
        // close open popovers and clean up all event listeners
        if (desktop.popoverVisible) {
            closePopover();
        }
        if (mobile.popoverVisible) {
            closeMobilePopover();
        }
        backBtn.classList.remove("enter-right");
        backBtn.classList.remove("leave-left");
        popover.classList.remove("no-transition");
        popover.classList.remove("enter");
        popover.classList.remove("leave");
        popover.style = "";
        root.style.top = "0px";

        window.removeEventListener("scroll", scroll);
        window.removeEventListener("resize", resize);
        window.removeEventListener("keydown", keyDown);

        popoverBg.removeEventListener("touchstart", closePopover);
        popoverBg.removeEventListener("mousedown", closePopover);
        popoverBg.removeEventListener("mousedown", closePopover);

        // remove all event listeners root and children
        root.innerHTML = root.innerHTML;
    }

    function remToPixels(rem) {
        return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
    }

    function setupLogos(cobrand) {
        // setup which logos to use based on transparency + cobrand settings
        settings.primaryLogo = settings.transparent ? "livingston-white" : "livingston-color";
        settings.secondaryLogo = settings.cobrand !== "" && !mobile.enabled ? `${cobrand}-color` : "livingston-color";
        setLogo(settings.primaryLogo);
    }

    function setSticky(sticky, stickyLimits) {
        settings.sticky = sticky;
        if (!sticky) {
            navBar.scrollTrigger = stickyLimits;
            if (settings.transparent) {
                if (navBar.scrollTrigger < fade.max + navBg.offsetHeight) {
                    navBar.scrollTrigger = fade.max + navBg.offsetHeight;
                }
            } else {
                if (navBar.scrollTrigger < remToPixels(4.5)) {
                    navBar.scrollTrigger = remToPixels(4.5);
                }
            }
        }
    }

    function keyDown(e) {
        // fullscreen browsing: alt + f
        if (e.keyCode === 70 && e.altKey) {
            setSticky(!settings.sticky, settings.stickyLimit);
        }
        // cobrand with direct logo: alt + d
        else if (e.keyCode === 68 && e.altKey) {
            settings.cobrand = settings.cobrand === "" ? "direct" : "";
            setupLogos(settings.cobrand);
        }
    }

    function scroll() {
        if (mobile.popoverVisible) return;
        if (desktop.popoverVisible) {
            closePopover();
        }

        if (settings.transparent) {
            const scrollY = Math.max(0, window.scrollY - fade.min);
            const t = Math.min(fade.max, scrollY) / fade.max;
            const scale = TimingFunctions.easeInOut(t);
            // const scale = TimingFunctions.easeInOutQuint(t);

            if (navBar.scale !== scale) {
                navBar.scale = scale;
                root.style.setProperty("--navBarScale", 1 - scale);
            }
        }

        // scrolled beyond threshold

        if (window.scrollY > fade.max * 0.75 && state.currentLogo !== settings.secondaryLogo) {
            setLogo(settings.secondaryLogo);
        }
        // scrolled to top switch to livingston logo
        else if (window.scrollY < fade.max * 0.75 && state.currentLogo !== settings.primaryLogo) {
            setLogo(settings.primaryLogo);
        }

        // scroll navbar if fullscreen scrolling enabled
        if (!settings.sticky && !navBar.locked) {
            const delta = window.scrollY - state.scrollY;
            state.scrollY = window.scrollY;
            scrollNav(delta);
        }
    }

    function scrollNav(delta) {
        navBar.height = navBg.offsetHeight;
        const trigger = navBar.scrollTrigger - navBar.height;

        // only update nav bar if:
        // • scrolling up and y > hidden pos or
        // • scrolling down and y < 0 (top)
        if ((delta < 0 && navBar.y < 0) || (window.scrollY > trigger && navBar.y > -navBar.height)) {
            if (mobile.enabled) {
                // mobile uses easing
                navBar.y -= delta / (delta < 0 ? 2 : 4);
                navBar.y = Math.min(Math.max(-navBar.height, navBar.y), 0);
                const dist = Math.max(0, -navBar.y);
                const t = Math.min(navBar.height, dist) / navBar.height;
                const py = TimingFunctions.easeIn(t);
                root.style.top = `${py * -navBar.height}px`;
            } else {
                // none mobile uses easing
                navBar.y -= delta / (delta < 0 ? 2 : 2);
                navBar.y = Math.min(Math.max(-navBar.height, navBar.y), 0);
                root.style.top = `${navBar.y}px`;
            }
        }
    }

    function resize() {
        const w = window.innerWidth;

        // trigger to switch between desktop and mobile versions
        if ((w > view.min && mobile.enabled) || (w <= view.min && !mobile.enabled)) {
            dispose();
            init();
            return;
        }
        // View scales along gradient as width progressed from max to min.
        // So at "max" width viewscale = 1, and at "min" width viewscale will be 0
        // This scalar value is used to set "--viewScale" custom css property that is
        // used to scale selective elements.
        let scalar;
        if (w > view.max) {
            // Desktop view scale: 1
            scalar = 1;
        } else if (w <= view.max && w > view.min) {
            // Desktop view scale: somewhere between min and max (view scale between 1 and 0)
            scalar = (w - view.min) / (view.max - view.min);
        } else {
            // mobile view scale: 1
            scalar = 1;
        }
        root.style.setProperty("--viewScale", scalar);
    }

    // sets popover to close after delay
    function delayedClose() {
        if (desktop.popoverVisible && !timeout) {
            timeout = setTimeout(closePopover, 250);
        }
    }

    // cancels any active close timeouts
    function cancelClose() {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
    }

    // returns the first menu item with matching data-id
    function menuWithId(id) {
        for (const menu of popoverContent.children) {
            if (menu.dataset.id === id) {
                return menu;
            }
        }
    }

    // hides/shows popover for selected menuItem
    function togglePopover(menuItem) {
        if (state.selected === menuItem) {
            desktop.popoverVisible ? closePopover() : openPopover(menuItem);
        } else {
            openPopover(menuItem);
        }
    }

    // opens the desktop version of the popover
    function openPopover(menuItem) {
        // clear prev selected icon-overview
        if (state.selected) state.selected.classList.remove("selected");

        const menu = menuWithId(menuItem.dataset.id);
        if (!menu) {
            closePopover();
            return;
        }

        cancelClose();
        state.selected = menuItem;

        // get dimensions of content that needs to be displayed
        const contentW = menu.offsetWidth;
        const contentH = menu.offsetHeight;

        const margin = settings.menuRelativeMargin;

        // constrain the window to the width of the nav
        let min = nav.children[0].getBoundingClientRect().left;
        let max = nav.children[nav.children.length - 1].getBoundingClientRect().right;
        const navWidth = max - min + margin * 2;
        min += margin;
        max -= margin;

        // get positions of elements
        const menuRect = menu.getBoundingClientRect();
        const buttonRect = state.selected.getBoundingClientRect();
        const containerRect = popoverContent.getBoundingClientRect();

        // center of the button to point the arro to
        const buttonW = buttonRect.right - buttonRect.left;
        const buttonCenterX = buttonRect.left + buttonW / 2;

        // calculate window placement
        let posX;
        const halfWidth = contentW / 2;
        if (contentW > navWidth) {
            // centered
            posX = window.innerWidth / 2 - halfWidth;
        } else {
            // aligned with button
            posX = buttonCenterX - halfWidth;
            if (posX < min) {
                posX = min;
            } else if (posX + contentW > max) {
                posX = max - contentW;
            }
        }

        // window scroll offset
        var scrollX = menuRect.left - containerRect.left;

        if (!desktop.popoverVisible) {
            arrow.classList.add("no-transition");
            popover.classList.add("no-transition");
            popoverContent.classList.add("no-transition");
        } else {
            arrow.classList.remove("no-transition");
            popover.classList.remove("no-transition");
            popoverContent.classList.remove("no-transition");
        }
        desktop.popoverVisible = true;

        // update dom
        arrow.style.left = `${buttonCenterX - arrow.offsetWidth / 2}px`;
        popover.style.left = `${posX}px`;
        popover.style.width = `${contentW}px`;
        popover.style.height = `${contentH}px`;
        popoverContent.style.left = `${-scrollX}px`;

        // show selected menu item
        state.selected.classList.add("selected");

        // clear previous animations
        arrow.classList.remove("leave");
        popover.classList.remove("leave");
        popoverBg.classList.remove("leave");

        // play enter animation
        arrow.classList.add("enter");
        popover.classList.add("enter");
        popoverBg.classList.add("enter");
    }

    function closePopover() {
        if (!desktop.popoverVisible) return;
        arrow.classList.remove("enter");
        popover.classList.remove("enter");
        popoverBg.classList.remove("enter");

        arrow.classList.add("leave");
        popover.classList.add("leave");
        popoverBg.classList.add("leave");

        if (state.selected) state.selected.classList.remove("selected");
        desktop.popoverVisible = false;
    }

    // opens / closes mobile popover drawer
    function toggleMobilePopover() {
        mobile.popoverVisible ? closeMobilePopover() : openMobilePopover();
    }

    // opens mobile popover drawer
    function openMobilePopover() {
        clearTimeout(timeout);
        navBar.locked = true;
        mobile.popoverVisible = true;
        mobilePopover.style.visibility = "visible";
        state.returnScrollPos = window.scrollY;

        if (mobile.subMenuVisible) {
            backBtn.classList.remove("leave-left");
            backBtn.classList.add("enter-right");
        } else {
            popover.classList.remove("sub-menu-leave");
            popover.classList.remove("sub-menu-enter");
            nav.classList.remove("sub-menu-leave");
            nav.classList.remove("sub-menu-enter");
        }

        const html = document.querySelector("html");
        const body = document.querySelector("body");
        html.classList.add("no-scroll");
        body.classList.add("no-scroll");

        smoothScrollTo(window.scrollY, state.returnScrollPos + 100, 400, TimingFunctions.easeInOut);
        mobilePopover.style.top = "0px";
        hamburgerBtn.classList.toggle("opened");
        navBg.classList.add("expanded");
        popoverBg.classList.remove("leave");
        popoverBg.classList.add("enter");
        setLogo("livingston-color");
    }

    // closes mobile popover drawer
    function closeMobilePopover() {
        mobile.popoverVisible = false;
        if (mobile.subMenuVisible) {
            backBtn.classList.remove("enter-right");
            backBtn.classList.add("leave-left");
        }

        smoothScrollTo(window.scrollY, state.returnScrollPos, 350, TimingFunctions.easeInOut);
        mobilePopover.style.top = `${window.innerHeight + 100}px`;
        hamburgerBtn.classList.toggle("opened");
        navBg.classList.remove("expanded");
        popoverBg.classList.remove("enter");
        popoverBg.classList.add("leave");

        const html = document.querySelector("html");
        const body = document.querySelector("body");
        html.classList.remove("no-scroll");
        body.classList.remove("no-scroll");

        timeout = setTimeout(() => {
            navBar.locked = false;
            mobilePopover.style.visibility = "hidden";
        }, 400);
    }

    function showMobileSubMenu(menuItem) {
        const menu = menuWithId(menuItem.dataset.id);
        if (!menu) return;

        mobile.subMenuVisible = true;
        const menuRect = menu.getBoundingClientRect();
        menu.scrollTop = 0;
        const containerRect = popoverContent.getBoundingClientRect();
        const scrollX = menuRect.left - containerRect.left;
        popoverContent.style.left = `${-scrollX}px`;

        popover.classList.remove("sub-menu-leave");
        popover.classList.add("sub-menu-enter");
        nav.classList.remove("main-menu-enter");
        nav.classList.add("main-menu-leave");
        backBtn.classList.remove("leave-left");
        backBtn.classList.add("enter-right");
    }

    function closeMobileSubMenu() {
        mobile.subMenuVisible = false;
        popover.classList.remove("sub-menu-enter");
        popover.classList.add("sub-menu-leave");
        nav.classList.remove("main-menu-leave");
        nav.classList.add("main-menu-enter");
        backBtn.classList.remove("enter-right");
        backBtn.classList.add("leave-left");
    }

    // handles logo change animation based on id
    function setLogo(id) {
        if (id === state.currentLogo) return;

        // get logo config
        const prev = LogoConfig[state.currentLogo];
        const next = LogoConfig[id];
        if (!next) return;

        // update logo if needed
        if (!prev || next.logo !== prev.logo) {
            if (next.logo === "direct") {
                logo.querySelector(".direct").classList = ["direct enter-bottom"];
                logo.querySelector(".livingston").classList = ["livingston leave-top"];
                logoBox.classList.add("center-direct-logo");
            } else {
                logo.querySelector(".direct").classList = ["direct leave-bottom"];
                logo.querySelector(".livingston").classList = ["livingston enter-top"];
                logoBox.classList.remove("center-direct-logo");
            }
        }
        // set arrow color if changed
        if (!prev || next.arrow !== prev.arrow) {
            logo.querySelector(".arrow.white").style.opacity = next.arrow === "white" ? 1 : 0;
            logo.querySelector(".arrow.orange").style.opacity = next.arrow === "orange" ? 1 : 0;
            logo.querySelector(".arrow.blue").style.opacity = next.arrow === "blue" ? 1 : 0;
        }
        // store updated state
        state.currentLogo = id;
    }

    function smoothScrollTo(from, to, duration = 300, timingFunc) {
        let start = window.performance.now();
        const delta = to - from;
        const step = (timestamp) => {
            const elapsed = timestamp - start;
            const time = Math.min(1, elapsed / duration);
            window.scrollTo(0, from + delta * timingFunc(time));
            if (elapsed < duration) window.requestAnimationFrame(step);
        };
        window.requestAnimationFrame(step);
    }

    function phone(phoneNumber) {
        window.open(`tel:${phoneNumber}`, "_self");
    }
};
